import React, { useState } from 'react';
import Joyride, { CallBackProps, STATUS } from 'react-joyride';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, makeStyles } from '@material-ui/core';

import { ApplicationState } from '../../store';
import { completeTour, loadDemoData } from '../../store/modules/user/actions';

const useStyles = makeStyles({
  welcomeTitle: {
    textAlign: 'center',
    paddingTop: '24px',
  },
  welcomeContent: {
    textAlign: 'center',
    padding: '8px 24px 16px',
  },
  welcomeActions: {
    justifyContent: 'center',
    padding: '8px 24px 24px',
    gap: '12px',
  },
  skipBtn: {
    color: '#ffffff88',
  },
});

const tourSteps = [
  {
    target: '#tour-home-btn',
    content: 'Este e o botao de inicio. Aqui voce volta para o painel principal com todas as suas estatisticas.',
    disableBeacon: true,
  },
  {
    target: '#tour-history-btn',
    content: 'Aqui voce visualiza o historico completo de todos os seus trades com filtros por data.',
    disableBeacon: true,
  },
  {
    target: '#tour-deposits-btn',
    content: 'Registre seus depositos e retiradas para manter seu saldo atualizado.',
    disableBeacon: true,
  },
  {
    target: '#tour-add-trade',
    content: 'Este e o botao mais importante! Clique aqui para registrar um novo trade. Preencha a data, ativo, resultado.',
    disableBeacon: true,
  },
];

const joyrideStyles = {
  options: {
    backgroundColor: '#2C325C',
    textColor: '#ffffffdd',
    primaryColor: '#ffee58',
    arrowColor: '#2C325C',
    overlayColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 10000,
  },
  buttonNext: {
    backgroundColor: '#ffee58',
    color: '#1E2141',
    fontWeight: 600,
    borderRadius: '6px',
    padding: '8px 20px',
  },
  buttonBack: {
    color: '#ffffffaa',
    marginRight: '8px',
  },
  buttonSkip: {
    color: '#ffffff88',
  },
  tooltip: {
    borderRadius: '10px',
    padding: '16px',
  },
  tooltipContent: {
    fontSize: '14px',
    lineHeight: '1.5',
    padding: '8px 0',
  },
};

export default function GuidedTour() {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { hasCompletedTour, hasDemoData } = useSelector(
    (state: ApplicationState) => state.user.user
  );
  const { loading, boxes } = useSelector(
    (state: ApplicationState) => state.boxes
  );

  const [showWelcome, setShowWelcome] = useState(!hasCompletedTour);
  const [runTour, setRunTour] = useState(false);
  const [showDemoPrompt, setShowDemoPrompt] = useState(false);

  const hasData = boxes && boxes.LastTrades && boxes.LastTrades.length > 0;

  const handleStartTour = () => {
    setShowWelcome(false);
    setRunTour(true);
  };

  const handleSkipAll = () => {
    setShowWelcome(false);
    dispatch(completeTour());
    if (!hasData && !hasDemoData) {
      setShowDemoPrompt(true);
    }
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    var status = data.status;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var action = data.action;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunTour(false);
      dispatch(completeTour());

      if (!hasData && !hasDemoData) {
        setShowDemoPrompt(true);
      }
    }
  };

  const handleLoadDemo = () => {
    setShowDemoPrompt(false);
    dispatch(loadDemoData());
  };

  const handleSkipDemo = () => {
    setShowDemoPrompt(false);
  };

  if (hasCompletedTour && !showDemoPrompt) {
    return null;
  }

  if (loading) {
    return null;
  }

  return (
    <>
      <Dialog
        open={showWelcome}
        onClose={handleSkipAll}
        PaperProps={{
          style: {
            backgroundColor: '#2C325C',
            color: '#ffffffdd',
            borderRadius: '12px',
            maxWidth: '420px',
          },
        }}
      >
        <DialogTitle className={classes.welcomeTitle}>
          Bem-vindo ao DashTrader!
        </DialogTitle>
        <DialogContent className={classes.welcomeContent}>
          <Typography style={{ fontSize: '15px', lineHeight: 1.6 }}>
            Que bom ter voce aqui! Vamos fazer um tour rapido para voce conhecer
            as principais funcionalidades do seu painel de trading.
          </Typography>
        </DialogContent>
        <DialogActions className={classes.welcomeActions}>
          <Button onClick={handleSkipAll} className={classes.skipBtn}>
            Pular
          </Button>
          <Button
            onClick={handleStartTour}
            variant="contained"
            style={{
              backgroundColor: '#ffee58',
              color: '#1E2141',
              fontWeight: 600,
              borderRadius: '6px',
            }}
          >
            Iniciar Tour
          </Button>
        </DialogActions>
      </Dialog>

      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        callback={handleJoyrideCallback}
        styles={joyrideStyles}
        locale={{
          back: 'Voltar',
          close: 'Fechar',
          last: 'Finalizar',
          next: 'Proximo',
          skip: 'Pular tour',
        }}
        floaterProps={{
          disableAnimation: true,
        }}
      />

      <Dialog
        open={showDemoPrompt}
        onClose={handleSkipDemo}
        PaperProps={{
          style: {
            backgroundColor: '#2C325C',
            color: '#ffffffdd',
            borderRadius: '12px',
            maxWidth: '420px',
          },
        }}
      >
        <DialogTitle className={classes.welcomeTitle}>
          Carregar dados de exemplo?
        </DialogTitle>
        <DialogContent className={classes.welcomeContent}>
          <Typography style={{ fontSize: '15px', lineHeight: 1.6 }}>
            Para voce ver como o painel funciona com dados reais, podemos carregar
            alguns trades de exemplo. Voce pode remove-los a qualquer momento.
          </Typography>
        </DialogContent>
        <DialogActions className={classes.welcomeActions}>
          <Button onClick={handleSkipDemo} className={classes.skipBtn}>
            Nao, obrigado
          </Button>
          <Button
            onClick={handleLoadDemo}
            variant="contained"
            style={{
              backgroundColor: '#ffee58',
              color: '#1E2141',
              fontWeight: 600,
              borderRadius: '6px',
            }}
          >
            Carregar exemplos
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
